import { Person } from "@mui/icons-material";
import { Box, Typography, Avatar, Button, Chip } from "@mui/material";
import { practicePatientManagementApi } from "apis";
import RequestPatientConfirmModal from "components/features/patient/RequestPatientConfirmModal";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import {
  PrescriptionPatientDto,
  PrescriptionPatientDtoStatusEnum,
} from "libs/cellera-sdk";
import { isNil } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { formatBirthDate } from "utils/date";

export interface PrescriptionPatientDetailsCardProps {
  patient: PrescriptionPatientDto;
  onRefreshed: () => Promise<void>;
}

export const PrescriptionPatientDetailsCard = ({
  patient,
  onRefreshed,
}: PrescriptionPatientDetailsCardProps) => {
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const [isConfirmRequestModalOpen, setIsConfirmRequestModalOpen] =
    useState(false);
  const isActiveSharing = useMemo(
    () => patient.status === PrescriptionPatientDtoStatusEnum.ActiveSharing,
    [patient.status]
  );
  const isAbleToRequestSharing = useMemo(
    () =>
      patient.status === PrescriptionPatientDtoStatusEnum.Revoked ||
      isNil(patient.status),
    [patient.status]
  );

  const handleRequestSubmit = useCallback(async () => {
    if (!patient?.email) return;
    try {
      await practicePatientManagementApi.requestPatientDataSharing({
        requestPatientDataSharingDto: {
          email: patient.email,
        },
      });

      showSuccess("Success", "Data sharing request sent successfully.");
      await onRefreshed();
    } catch (error) {
      showError(
        "Error",
        error instanceof Error
          ? error.message
          : "Failed to request data sharing."
      );
    } finally {
      setIsConfirmRequestModalOpen(false);
    }
  }, [patient.email, showError, showSuccess, onRefreshed]);

  const patientDetails = useMemo(() => {
    return {
      name:
        [patient.firstName, patient.lastName]
          .filter(Boolean)
          .join(" ")
          .trim() || "N/A",
      email: patient.email,
      phone: isActiveSharing ? patient.phone : "N/A",
      status: patient.status,
      birthDate: isActiveSharing ? patient.birthDate : null,
    };
  }, [
    patient.firstName,
    patient.lastName,
    patient.email,
    patient.phone,
    patient.status,
    patient.birthDate,
    isActiveSharing,
  ]);

  const getStatusColor = useCallback(
    (status?: PrescriptionPatientDtoStatusEnum) => {
      switch (status) {
        case PrescriptionPatientDtoStatusEnum.ActiveSharing:
          return {
            border: `1px solid ${colors.success[700]}`,
            backgroundColor: colors.success[100],
            color: colors.success[700],
          };
        case PrescriptionPatientDtoStatusEnum.Revoked:
          return {
            border: `1px solid ${colors.error[700]}`,
            backgroundColor: colors.error[100],
            color: colors.error[700],
          };
        case PrescriptionPatientDtoStatusEnum.Requesting:
          return {
            border: `1px solid ${colors.warning[700]}`,
            backgroundColor: colors.warning[100],
            color: colors.warning[700],
          };
        default:
          return {
            border: `1px solid ${colors.border.main}`,
            backgroundColor: colors.background.secondary,
            color: colors.text.secondary,
          };
      }
    },
    [colors]
  );

  const getStatusLabel = useCallback(
    (status?: PrescriptionPatientDtoStatusEnum) => {
      switch (status) {
        case PrescriptionPatientDtoStatusEnum.ActiveSharing:
          return "ACTIVE SHARING";
        case PrescriptionPatientDtoStatusEnum.Revoked:
          return "REVOKED";
        case PrescriptionPatientDtoStatusEnum.Requesting:
          return "REQUESTING";
        default:
          return "N/A";
      }
    },
    []
  );

  const formattedBirthDate = useMemo(() => {
    if (!patientDetails?.birthDate) return "N/A";
    try {
      const date =
        patientDetails.birthDate instanceof Date
          ? patientDetails.birthDate
          : new Date(patientDetails.birthDate);
      return formatBirthDate(date);
    } catch {
      return "N/A";
    }
  }, [patientDetails?.birthDate]);

  const patientFullName = useMemo(() => {
    if (!patient) return "";
    return [patient.firstName, patient.lastName].filter(Boolean).join(" ");
  }, [patient]);

  return (
    <Box
      sx={{
        border: `1px solid ${colors.border.main}`,
        borderRadius: 2,
        p: 3,
        mb: 3,
        bgcolor: colors.background.paper,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: colors.success[700],
            color: colors.text.inverse,
            fontWeight: 700,
          }}
        >
          {patient.avatarUrl ? (
            <img
              src={patient.avatarUrl}
              alt={patientFullName}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Person sx={{ fontSize: 30 }} />
          )}
        </Avatar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
            flex: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.text.primary,
              fontWeight: 700,
            }}
          >
            {patientFullName || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 500 }}
          >
            Email: {patient.email || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 500 }}
          >
            Birthday: {formattedBirthDate}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 0.5,
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 500 }}
          >
            Phone: {patient.phone || "N/A"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, fontWeight: 500 }}
          >
            Status:
            <Chip
              label={getStatusLabel(patientDetails.status)}
              size="small"
              sx={{
                ...getStatusColor(patientDetails.status),
                ml: 1,
                fontSize: 11,
                fontWeight: 600,
                height: 24,
                borderRadius: 0,
              }}
            />
          </Typography>
        </Box>

        {isActiveSharing && (
          <Button
            variant="outlined"
            onClick={() =>
              navigate(
                EPath.PATIENT_DASHBOARD.replace(":patientId", patient.patientId)
              )
            }
            sx={{
              textTransform: "none",
              borderColor: colors.border.main,
              color: colors.text.primary,
              fontWeight: 600,
              borderRadius: "100px",
              px: 3,
              "&:hover": {
                borderColor: colors.primary.main,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            View Details
          </Button>
        )}
        {isAbleToRequestSharing && (
          <Button
            variant="outlined"
            onClick={() => setIsConfirmRequestModalOpen(true)}
            sx={{
              textTransform: "none",
              borderColor: colors.border.main,
              color: colors.text.primary,
              fontWeight: 600,
              borderRadius: "100px",
              px: 3,
              "&:hover": {
                borderColor: colors.primary.main,
                backgroundColor: colors.background.secondary,
              },
            }}
          >
            Request Data Sharing
          </Button>
        )}
      </Box>
      <RequestPatientConfirmModal
        open={isConfirmRequestModalOpen}
        onClose={() => setIsConfirmRequestModalOpen(false)}
        onSubmit={handleRequestSubmit}
        patientEmail={patient.email}
      />
    </Box>
  );
};
